// TEMPORARY on-device login diagnostics.
//
// When LOGIN_DEBUG_ALERTS is true, loginDebugAlert() paints each login milestone + any error onto an
// on-screen overlay pinned to document.body. This is for diagnosing login failures on cloud emulators /
// user devices where pulling logs is painful - you literally watch how far the flow gets and what it
// throws, step by step.
//
// Why an overlay instead of window.alert(): the Android Capacitor WebView silently drops window.alert()
// when it fires during the return from the Okta Custom Tab / mid-navigation, so the alerts never showed.
// This overlay is appended straight to document.body (outside the Angular app root), so it renders
// regardless of WebView alert support AND survives route changes - including the navigate to /error - so
// the full step trail stays visible on top of whatever screen the app lands on.
//
// >>> SET LOGIN_DEBUG_ALERTS BACK TO false BEFORE SHIPPING TO REAL USERS. <<<
// Typed as boolean (not the literal `true`/`false`) so flipping this value never trips
// "unreachable code" / "condition is always ..." lint rules in the function body or call sites.
export const LOGIN_DEBUG_ALERTS: boolean = false;

const OVERLAY_ID = 'login-debug-overlay';

function getOverlay(): HTMLElement | null {
  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    return overlay;
  }

  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute(
    'style',
    [
      'position:fixed',
      'top:0',
      'left:0',
      'right:0',
      'z-index:2147483647', // max - sit above every app/modal layer
      'max-height:60vh',
      'overflow-y:auto',
      'background:rgba(0,0,0,0.88)',
      'color:#fff',
      'font:12px/1.4 monospace',
      'padding:8px 10px 10px',
      'white-space:pre-wrap',
      'word-break:break-word',
      '-webkit-user-select:text',
      'user-select:text',
    ].join(';')
  );

  // A dismiss button so the overlay can be cleared once you've read it.
  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'x clear';
  closeBtn.setAttribute(
    'style',
    [
      'position:sticky',
      'top:0',
      'float:right',
      'background:#c00',
      'color:#fff',
      'border:0',
      'border-radius:3px',
      'padding:2px 8px',
      'font:12px/1.4 monospace',
    ].join(';')
  );
  closeBtn.onclick = () => overlay?.remove();
  overlay.appendChild(closeBtn);

  document.body.appendChild(overlay);
  return overlay;
}

export function loginDebugAlert(step: string, detail?: any): void {
  if (!LOGIN_DEBUG_ALERTS) {
    return;
  }
  try {
    let detailText = '';
    if (detail !== undefined && detail !== null) {
      if (typeof detail === 'string') {
        detailText = detail;
      } else {
        try {
          detailText = JSON.stringify(detail);
        } catch {
          detailText = String(detail);
        }
      }
    }

    // Always log to console too, in case the overlay itself can't mount.
    console.warn(`[LOGIN] ${step}${detailText ? ' - ' + detailText : ''}`);

    const overlay = getOverlay();
    if (!overlay) {
      return;
    }

    const line = document.createElement('div');
    line.setAttribute('style', 'margin-top:6px;border-top:1px solid rgba(255,255,255,0.2);padding-top:6px');
    line.textContent = `[LOGIN] ${step}${detailText ? '\n' + detailText : ''}`;
    overlay.appendChild(line);

    // Keep the newest entry in view.
    overlay.scrollTop = overlay.scrollHeight;
  } catch {
    // Never let diagnostics break the flow.
  }
}
